// ArticlesPage.js
import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc, addDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { Loader, AlertCircle, Plus, Save, Trash, Image } from 'lucide-react';
import RichTextEditor from './RichTextEditor';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const storage = getStorage();

const ArticlesPage = () => {
 const [articles, setArticles] = useState([]);
 const [subjects, setSubjects] = useState([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState(null);
 const [saveStatus, setSaveStatus] = useState({ show: false, message: '', type: '' });
 const [pendingChanges, setPendingChanges] = useState({});
 const [editingArticleId, setEditingArticleId] = useState(null);
 const [imageUpload, setImageUpload] = useState(null);

 useEffect(() => {
   loadData();
 }, []);

 const loadData = async () => {
   try {
     const subjectsSnapshot = await getDocs(collection(db, 'subjects'));
     const subjectsData = subjectsSnapshot.docs.map(doc => ({
       id: doc.id,
       ...doc.data()
     }));
     setSubjects(subjectsData);
 
     const articlesSnapshot = await getDocs(collection(db, 'articles'));
     const articlesData = articlesSnapshot.docs.map(doc => ({
       id: doc.id,
       ...doc.data()
     }));
     setArticles(articlesData);
   } catch (err) {
     setError('אירעה שגיאה בטעינת הנתונים');
     console.error(err);
   } finally {
     setLoading(false);
   }
 };

 const uploadImage = async (file, articleId) => {
   if (!file) return null;
   const imageRef = ref(storage, `articles/${articleId}/${file.name}`);
   await uploadBytes(imageRef, file);
   return await getDownloadURL(imageRef);
 };

 const handleArticleChange = (articleId, field, value) => {
   setPendingChanges(prev => ({
     ...prev,
     [articleId]: {
       ...prev[articleId],
       [field]: value
     }
   }));
 };

 const handleAddArticle = async () => {
   try {
     const newArticle = {
       title: '',
       subjectId: subjects[0]?.id || '',
       content: '',
       imageUrl: '',
       createdAt: new Date().toISOString()
     };

     const docRef = await addDoc(collection(db, 'articles'), newArticle);
     setArticles(prev => [...prev, { id: docRef.id, ...newArticle }]);
   } catch (err) {
     setError('שגיאה בהוספת מאמר חדש');
     console.error(err);
   }
 };

 const handleSave = async (articleId) => {
   if (!pendingChanges[articleId]) return;

   setEditingArticleId(articleId);
   setSaveStatus({ show: true, message: 'שומר שינויים...', type: 'info' });

   try {
     let imageUrl = pendingChanges[articleId]?.imageUrl;
     
     if (imageUpload) {
       imageUrl = await uploadImage(imageUpload, articleId);
       setImageUpload(null);
     }

     const articleRef = doc(db, 'articles', articleId);
     const updatedData = {
       ...pendingChanges[articleId],
       updatedAt: new Date().toISOString()
     };
     
     if (imageUrl) {
       updatedData.imageUrl = imageUrl;
     }

     await updateDoc(articleRef, updatedData);
     
     setArticles(articles.map(article => 
       article.id === articleId ? { ...article, ...updatedData } : article
     ));

     setPendingChanges(prev => {
       const newChanges = { ...prev };
       delete newChanges[articleId];
       return newChanges;
     });

     setSaveStatus({ show: true, message: 'נשמר בהצלחה!', type: 'success' });
   } catch (err) {
     setSaveStatus({ show: true, message: 'שגיאה בשמירת הנתונים', type: 'error' });
     console.error(err);
   } finally {
     setEditingArticleId(null);
     setTimeout(() => setSaveStatus({ show: false }), 3000);
   }
 };

 const handleDelete = async (articleId) => {
   if (!window.confirm('האם אתה בטוח שברצונך למחוק מאמר זה?')) return;

   try {
     await deleteDoc(doc(db, 'articles', articleId));
     setArticles(articles.filter(article => article.id !== articleId));
     setSaveStatus({ show: true, message: 'המאמר נמחק בהצלחה', type: 'success' });
   } catch (err) {
     setSaveStatus({ show: true, message: 'שגיאה במחיקת המאמר', type: 'error' });
     console.error(err);
   }
   setTimeout(() => setSaveStatus({ show: false }), 3000);
 };

 if (loading) {
   return (
     <div className="flex items-center justify-center min-h-[400px]">
       <Loader className="w-8 h-8 animate-spin text-blue-500" />
     </div>
   );
 }

 if (error) {
   return (
     <div className="flex items-center justify-center min-h-[400px] text-red-500">
       <AlertCircle className="w-6 h-6 mr-2" />
       <span>{error}</span>
     </div>
   );
 }

 return (
   <div className="max-w-4xl mx-auto">
     <div className="flex justify-between items-center mb-6">
       <h1 className="text-xl md:text-2xl font-bold">ניהול מאמרים</h1>
       <button
         onClick={handleAddArticle}
         className="bg-blue-600 text-white px-4 py-2 rounded-md flex items-center gap-2 hover:bg-blue-700 transition-colors"
       >
         <Plus className="w-4 h-4" />
         <span>מאמר חדש</span>
       </button>
     </div>

     {saveStatus.show && (
       <div className={`
         mb-6 px-4 py-2 rounded-lg text-sm md:text-base
         ${saveStatus.type === 'success' ? 'bg-green-100 text-green-700' : ''}
         ${saveStatus.type === 'error' ? 'bg-red-100 text-red-700' : ''}
         ${saveStatus.type === 'info' ? 'bg-blue-100 text-blue-700' : ''}
       `}>
         {saveStatus.message}
       </div>
     )}

     <div className="space-y-6">
       {articles.map(article => (
         <div key={article.id} className="bg-white p-6 rounded-lg shadow-md">
           <div className="space-y-4">
           {/* תמונת מאמר */}
<div>
  <label className="block text-sm font-medium text-gray-700 mb-2">
    תמונת מאמר
  </label>
  <div className="flex items-center gap-4">
    <input
      type="file"
      accept="image/*"
      onChange={(e) => {
        setImageUpload(e.target.files[0]);
        handleArticleChange(article.id, 'imageUrl', null);
      }}
      className="w-full p-2 border rounded-md"
    />
    {(article.imageUrl || pendingChanges[article.id]?.imageUrl) && (
      <img 
        src={pendingChanges[article.id]?.imageUrl || article.imageUrl}
        alt="תצוגה מקדימה"
        className="w-24 h-24 object-cover rounded"
      />
    )}
  </div>
</div>

             {/* כותרת המאמר */}
             <div>
               <label className="block text-sm font-medium text-gray-700 mb-2">
                 כותרת המאמר
               </label>
               <RichTextEditor
                 content={pendingChanges[article.id]?.title ?? article.title}
                 onChange={(newContent) => handleArticleChange(article.id, 'title', newContent)}
               />
             </div>

             {/* בחירת תחום */}
             <div>
               <label className="block text-sm font-medium text-gray-700 mb-2">
                 תחום
               </label>
               <select
                 value={pendingChanges[article.id]?.subjectId ?? article.subjectId}
                 onChange={(e) => handleArticleChange(article.id, 'subjectId', e.target.value)}
                 className="w-full p-2 border rounded-md"
               >
                 <option value="">בחר תחום</option>
                 {subjects.map(subject => (
                   <option key={subject.id} value={subject.id}>
                     {subject.name}
                   </option>
                 ))}
               </select>
             </div>

             {/* תוכן המאמר */}
             <div>
               <label className="block text-sm font-medium text-gray-700 mb-2">
                 תוכן המאמר
               </label>
               <RichTextEditor
                 content={pendingChanges[article.id]?.content ?? article.content}
                 onChange={(newContent) => handleArticleChange(article.id, 'content', newContent)}
               />
             </div>

             <div className="flex justify-between items-center">
               {/* כפתור שמירה */}
               {(pendingChanges[article.id] || (imageUpload && article.id)) && (
  <button
    onClick={() => handleSave(article.id)}
    disabled={editingArticleId === article.id}
    className={`
      px-4 py-2 rounded-md
      flex items-center gap-2
      ${editingArticleId === article.id 
        ? 'bg-blue-300 cursor-not-allowed'
        : 'bg-blue-600 hover:bg-blue-700'
      }
      text-white font-medium transition-colors
    `}
  >
    <Save className="w-4 h-4" />
    <span>{editingArticleId === article.id ? 'שומר...' : 'שמור שינויים'}</span>
  </button>
)}

               {/* כפתור מחיקה */}
               <button
                 onClick={() => handleDelete(article.id)}
                 className="text-red-600 hover:text-red-700 flex items-center gap-2"
               >
                 <Trash className="w-4 h-4" />
                 <span>מחק מאמר</span>
               </button>
             </div>
           </div>
         </div>
       ))}
     </div>
   </div>
 );
};

export default ArticlesPage;